import { motion } from "framer-motion";
import { ArrowRight, Phone, Users, Building2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TRUST_CARDS } from "@/data/content";
import { CONTACT } from "@/data/content";

export default function Hero({ onCheckEligibility }) {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden"
    >
      {/* Ambient background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 right-0 w-[520px] h-[520px] rounded-full bg-[#1E5AA8]/5 blur-3xl" />
        <div className="absolute -top-20 left-20 w-[380px] h-[380px] rounded-full bg-[#D4AF37]/10 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white/70 backdrop-blur px-3 py-1.5 mb-6"
            >
              <span className="w-2 h-2 rounded-full bg-[#16A34A] animate-pulse" />
              <span className="text-xs font-semibold text-gray-700 tracking-wide">
                RBI-regulated banking partners
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] text-[#0F2D52]"
            >
              Get the right loan through India&rsquo;s leading{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Banks &amp; NBFCs</span>
                <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#D4AF37]/40 -z-0 rounded-sm" />
              </span>
              .
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 text-base sm:text-lg text-gray-600 max-w-2xl leading-relaxed"
            >
              Money Mart is a Bank Channel Partner (DSA) that helps individuals and businesses compare suitable loan options,
              prepare documentation and complete the application process through our trusted banking partners.
              <span className="block mt-2 text-sm text-gray-500">
                We do not lend money directly.
              </span>
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <Button
                data-testid="hero-cta-check-eligibility"
                onClick={onCheckEligibility}
                className="rounded-full h-12 px-6 bg-[#0F2D52] hover:bg-[#0a1f38] text-white font-semibold text-sm shadow-[0_10px_30px_rgba(15,45,82,0.25)]"
              >
                Check Your Eligibility
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <a href={`tel:+${CONTACT.phoneDigits}`} data-testid="hero-cta-talk-expert">
                <Button
                  variant="outline"
                  className="rounded-full h-12 px-6 border-gray-300 text-[#0F2D52] hover:bg-gray-50 font-semibold text-sm w-full sm:w-auto"
                >
                  <Phone className="mr-2 h-4 w-4" />
                  Talk to an Expert
                </Button>
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="mt-8 flex items-center gap-4 text-xs text-gray-500"
            >
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-[#16A34A]" />
                No obligation enquiry
              </div>
              <div className="hidden sm:flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-[#16A34A]" />
                Subject to bank approval
              </div>
              <div className="hidden md:flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-[#16A34A]" />
                Data handled confidentially
              </div>
            </motion.div>
          </div>

          {/* Right — illustration */}
          <div className="lg:col-span-5">
            <HeroIllustration />

            {/* Trust cards row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4 gap-3 mt-6">
              {TRUST_CARDS.map((c, i) => (
                <motion.div
                  key={c.label}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.4 + i * 0.06 }}
                  data-testid={`trust-card-${i}`}
                  className="rounded-2xl bg-white border border-gray-200/70 p-3 shadow-[0_2px_8px_rgba(0,0,0,0.03)]"
                >
                  <div className="text-lg font-extrabold text-[#0F2D52] font-heading tracking-tight">
                    {c.value}
                  </div>
                  <div className="text-[11px] text-gray-500 mt-0.5 leading-tight">
                    {c.label}
                  </div>
                </motion.div>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-2 leading-relaxed">
              *Approval depends on lender eligibility criteria.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroIllustration() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.7, delay: 0.2 }}
      className="relative aspect-[5/5] rounded-3xl bg-gradient-to-br from-[#F8FAFC] to-white border border-gray-200/70 p-6 overflow-hidden shadow-[0_12px_40px_rgba(15,45,82,0.08)]"
    >
      {/* soft gold blob */}
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#D4AF37]/15 blur-3xl" />
      <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-[#1E5AA8]/10 blur-3xl" />

      {/* Customer card */}
      <FloatingCard
        style={{ top: "6%", left: "6%" }}
        delay={0.3}
        icon={<Users className="h-4 w-4 text-[#1E5AA8]" />}
        label="Customer"
        title="Ananya S."
        sub="Salaried · ₹18L p.a."
      />
      {/* Advisor card */}
      <FloatingCard
        style={{ top: "34%", right: "8%" }}
        delay={0.45}
        icon={<ShieldCheck className="h-4 w-4 text-[#0F2D52]" />}
        label="Money Mart Advisor"
        title="Rohit K."
        sub="Home Loan · Matching"
      />
      {/* Banks card */}
      <FloatingCard
        style={{ bottom: "22%", left: "8%" }}
        delay={0.6}
        icon={<Building2 className="h-4 w-4 text-[#1E5AA8]" />}
        label="Partner Banks"
        title="6 Suitable Lenders"
        sub="Comparing offers"
      />
      {/* Approval card */}
      <FloatingCard
        style={{ bottom: "6%", right: "6%" }}
        delay={0.75}
        icon={<CheckCircle2 className="h-4 w-4 text-[#16A34A]" />}
        label="Lender Decision"
        title="Application Submitted"
        sub="Subject to bank approval"
        accent
      />

      {/* Connecting line SVG */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <path
          d="M 18 15 Q 55 25 82 40 Q 40 55 18 78 Q 60 85 82 90"
          stroke="#1E5AA8"
          strokeOpacity="0.18"
          strokeWidth="0.4"
          strokeDasharray="1 1"
          fill="none"
        />
      </svg>
    </motion.div>
  );
}

function FloatingCard({ style, delay, icon, label, title, sub, accent }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      style={style}
      className={`absolute w-[54%] sm:w-[52%] max-w-[240px] rounded-2xl bg-white border border-gray-200 p-3 shadow-[0_10px_30px_rgba(15,45,82,0.10)] ${
        accent ? "ring-1 ring-[#D4AF37]/40" : ""
      }`}
    >
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-[9px] font-bold tracking-[0.14em] uppercase text-gray-500 truncate">
            {label}
          </div>
          <div className="text-sm font-semibold text-[#0F2D52] leading-tight truncate">
            {title}
          </div>
        </div>
      </div>
      <div className="mt-2 text-[11px] text-gray-500 leading-tight truncate">{sub}</div>
    </motion.div>
  );
}
